import {
  getNestedSubelementsByType,
  getNestedSubelementsById,
  getNextUuid,
  getMostRecentFieldValue,
  getMostRecentFieldValueInArray,
  getSexTranslatedOrOrig,
  getSexEnumFromResource,
  fieldToElementTypeMap,
  countSupersOfSharedField,
} from '@fs/zion-sls'
import { getSlsNameTypeByKey, getDefaultNameTemplate } from './helpers'
import { namePartTypeKeys } from '../../shared/utils/helpers'

const getIntermediateElementOfNameByType = ({ name, field, elementType }) => {
  if (!field || !elementType) return null

  const superElementIds = field?.superElements?.map((superElement) => superElement?.id)?.filter(Boolean) || []

  const matchingSuperElement = superElementIds
    .map((superElementId) => getNestedSubelementsById([name], superElementId)?.[0])
    .find((superElement) => superElement?.elementType === elementType)

  return matchingSuperElement || null
}

/**
 * Importing typedefs for better type hinting and documentation.
 *
 * @typedef {import('./helpers').NamePartTemplate} NamePartTemplate
 * @typedef {import('./helpers').NameTemplate} NameTemplate
 */

/**
 * Extracts linked persons from a name field's superElements hierarchy
 * For names, the hierarchy is: PERSON → NAME → Intermediate (NAME_GIVEN) → Field (NAME_GN)
 *
 * Handles two sharing scenarios:
 * 1. Old data: Intermediate element (NAME_GIVEN) is shared → points to multiple NAME elements → multiple PERSONs
 * 2. New data: Field (NAME_GN) is shared → has multiple superElements (intermediates) → each leads to different NAME/PERSON
 *
 * @param {Object} field - SLS field object (leaf node) with superElements array
 * @param {Array} images - The array of images from the SLS state
 * @returns {Array<{id: string, partitionKey: string}>} Array of linked person objects with id and partitionKey
 */
const getLinkedPersonsFromNameField = (field, images) => {
  if (!field?.superElements || field.superElements.length === 0) return []

  const linkedPersonsMap = new Map()

  // Start with personId directly on the field, if it exists
  if (field.personId) {
    linkedPersonsMap.set(field.personId, {
      id: field.personId,
      partitionKey: field.partitionKey,
    })
  }

  field.superElements.forEach((intermediate) => {
    const intermediateElement = getNestedSubelementsById(images, intermediate.id)?.[0]
    if (!intermediateElement?.superElements) return

    intermediateElement.superElements.forEach((name) => {
      const nameElement = getNestedSubelementsById(images, name.id)?.[0]
      if (nameElement?.personId) {
        linkedPersonsMap.set(nameElement.personId, {
          id: nameElement.personId,
          partitionKey: nameElement.partitionKey,
        })
      }
    })
  })

  return Array.from(linkedPersonsMap.values())
}

/**
 * Prepares an object containing parts of a name, keyed by their type.
 * For each type in `namePartTypeKeys`, attempts to extract the corresponding part from the `name` object,
 * and constructs an object with metadata such as elementId, fieldId, type, value, and status flags.
 *
 * @param {Object} name - The name object containing subelements to be parsed.
 * @param {Array} images - The array of images from the SLS state, used for determining shared fields.
 * @returns {NamePartTemplate} An object mapping each found name part type to its metadata.
 */
const prepareNameParts = (name, images) => {
  const availableNameParts = namePartTypeKeys.reduce((acc, elementType, index) => {
    const field = getNestedSubelementsByType(name, elementType)?.[0]
    const slsElementType = fieldToElementTypeMap[elementType] // Get the corresponding intermediate element type for the field type
    const part = getIntermediateElementOfNameByType({ name, field, elementType: slsElementType }) || {} // Get the intermediate element (e.g., NAME_GIVEN, NAME_SURNAME) that contains the field
    const fieldType = namePartTypeKeys[index] // Get corresponding field type

    if (field) {
      const elementId = part?.id || getNextUuid()
      const superElements = field?.superElements || []

      acc[fieldType] = {
        elementId, // ID of the intermediate element (for REM_SUB)
        id: field?.id || getNextUuid(), // ID of the field element (for ADD_FV)
        added: !part?.id,
        type: field?.fieldType || fieldType, // Use field type from field element or fallback to fieldType
        removed: false,
        value: getMostRecentFieldValue(field) || '',
        dispatches: [], // Placeholder for any dispatches related to this name part (e.g., for highlights integration)
        shared: countSupersOfSharedField(field, images) > 1, // Determine if the field is shared based on the number of super elements it has in relation to the images
        displayLabel: field?.label,
        superElements, // Store super elements for later reference (e.g., when determining if a field is shared)
        linkedPersonIds: getLinkedPersonsFromNameField(field, images), // Pre-compute linked persons ({id, partitionKey}) from superElements hierarchy
      }
    }
    return acc
  }, {})

  return availableNameParts
}

/**
 * Prepares an array of name objects based on the provided names and SLS types.
 *
 * @param {Array} names - The array of SLS name objects to process.
 * @param {Array<Object>} slsTypes - The array of SLS types.
 * @param {Array} images - The array of images from the SLS state, used for determining shared fields.
 * @param {Object} person - The person object to associate with the names.
 * @returns {Array<NameTemplate>} An array of processed name objects.
 */
export const prepareNames = (names, slsTypes, images, person) => {
  if (!names || names.length === 0) {
    const defaultNameTemplate = getDefaultNameTemplate(person)
    return [defaultNameTemplate]
  }

  return names.map((name) => {
    const slsNameType = getSlsNameTypeByKey(slsTypes, name?.nameType) || {}
    const partTypes =
      name?.subElements
        ?.flatMap((part) => part?.subElements?.flatMap((subPart) => subPart?.fieldType))
        .filter(Boolean) || []

    return {
      id: name?.id || getNextUuid(),
      added: !name?.id,
      removed: false,
      type: { key: slsNameType?.nameType, primaryText: slsNameType?.displayLabel },
      parts: partTypes,
      removedParts: [],
      ...prepareNameParts(name, images),
    }
  })
}

/**
 * @typedef {Object} PersonalDetailsTemplate
 * @property {string} id - Unique identifier for the personal detail field. Generated using getNextUuid().
 * @property {boolean} added - Indicates if the personal detail field has been added (newly created).
 * @property {Object} value - The value of the personal detail field. Can be an object or string depending on the field.
 *
 * @typedef {Object} EssentialInformationDefaultValues
 * @property {string} id - The unique identifier for the person. Generated using getNextUuid().
 * @property {boolean} added - Indicates if the person is newly added.
 * @property {boolean} primaryPerson - Indicates if the person is the primary person.
 * @property {PersonalDetailsTemplate} SEX_CODE - The sex field.
 * @property {PersonalDetailsTemplate} RACE_OR_COLOR - The race or color field.
 * @property {PersonalDetailsTemplate} AGE - The age field.
 * @property {Array<NameTemplate>} names - The prepared names array.
 * @property {Array} removedNames - An array of removed names (initially empty).
 */

/**
 * Prepares the default values for essential information about a person.
 *
 * This is the main interface for SLS data coming from the backend/Slscontext to be used in the Essential Information Card.
 * For new persons (without an existing ID), this function generates a temporary placeholder ID that will be replaced
 * with an actual ARK ID from the backend API during form submission.
 *
 * @param {Object} person - The person object containing information to parse.
 * @param {Array} slsTypes - An array of SLS types used for name preparation.
 * @param {Function} t - Translation function for localization. The result from the useTranslation hook.
 * @param {object} genderTranslations - translations object
 * @param {boolean} [isPrimaryPerson=false] - Indicates if the person is the primary person.
 * @param {boolean} [forcePrimary=false] - If resulting person.primary value should be true.
 * @param {Array} images - The array of images from the SLS state, used for determining shared fields.
 * @returns {EssentialInformationDefaultValues} An object containing the prepared essential information fields.
 */
export const prepareEssentialInformationDefaultValues = (
  person,
  slsTypes,
  t,
  genderTranslations,
  images,
  isPrimaryPerson = false,
  forcePrimary = false
) => {
  const primaryPerson = forcePrimary || (person?.primary ?? isPrimaryPerson)

  const sexSlsField = getNestedSubelementsByType(person, 'SEX_CODE')?.[0] || null
  const mostRecentFieldValue = getMostRecentFieldValueInArray(sexSlsField)
  const normalizedValue = mostRecentFieldValue?.normalizedValues?.[0] || null
  const standard = normalizedValue
    ? { standardizedText: normalizedValue?.text, formalText: normalizedValue?.resource }
    : null

  const sexValue = sexSlsField
    ? {
        primaryText: getSexTranslatedOrOrig(sexSlsField, genderTranslations) || '',
        key: getSexEnumFromResource(sexSlsField) || '',
        standard,
      }
    : undefined

  const raceSlsField = getNestedSubelementsByType(person, 'RACE_OR_COLOR')?.[0] || null
  const raceValue = getMostRecentFieldValue(raceSlsField) || ''

  const ageElement = getNestedSubelementsByType(person, 'AGE')?.[0] || null
  const ageField = ageElement?.subElements ? getNestedSubelementsByType(ageElement, 'AGE', true)?.[0] : ageElement
  const ageValue = getMostRecentFieldValue(ageField) || ''
  const ageLinkedPersonIds = ageElement ? ageElement.superElements : ageField?.superElements || []

  const names = getNestedSubelementsByType(person, 'NAME')
  const preparedNames = prepareNames(names, slsTypes, images, person)

  // Generate person ID: use existing ID or create a temporary placeholder ID
  // that will be replaced with a real ARK ID from the backend API during form submission
  let personId
  if (person?.id) {
    personId = person.id
  } else {
    // Generate a temporary ARK-format placeholder ID for new persons
    // This will be replaced by calling getNextArkId('person') during form submission
    const placeholderUuid = getNextUuid()
    personId = `temp:placeholder:${placeholderUuid}`
  }

  return {
    id: personId,
    recordId: person?.recordId || null,
    added: !person?.id,
    primaryPerson,
    SEX_CODE: {
      id: sexSlsField?.id || getNextUuid(),
      added: !sexSlsField?.id,
      value: sexValue,
      dispatches: [],
      shared: sexSlsField?.superElements?.length > 1, // If the field has more than 1 super element, it is shared
      linkedPersonIds: sexSlsField?.superElements || [],
    },
    RACE_OR_COLOR: {
      id: raceSlsField?.id || getNextUuid(),
      added: !raceSlsField?.id,
      value: raceValue,
      dispatches: [],
      shared: raceSlsField?.superElements?.length > 1,
      linkedPersonIds: raceSlsField?.superElements || [],
    },
    AGE: {
      id: ageField?.id || getNextUuid(),
      added: !ageField?.id,
      value: ageValue,
      dispatches: [],
      shared: ageLinkedPersonIds.length > 1,
      linkedPersonIds: ageLinkedPersonIds,
    },
    names: preparedNames,
    removedNames: [],
  }
}
